/**
 * Immunity, Weakness and Resistance (Foundry: rule-element/iwr): add (or remove) entries in the
 * Character's IWR block, merged with what is already there as Foundry merges them.
 */
import { createIWR, isKnownIWRType, sameIWR, type IWRData, type IWRException, type IWRKind } from "../iwr";
import { isValidPredicate, type RawPredicate } from "../predicate";
import { cleanRuleValue, resolveInjectedProperties, resolveValue } from "../resolve";
import type { EngineState } from "../state";
import type { RuleHandler, RuleInstance } from "./base";

export interface IWRRuleData {
  kind: IWRKind;
  mode: "add" | "remove";
  types: string[];
  definition: RawPredicate | null;
  exceptions: IWRException[];
  doubleVs: IWRException[];
  override: boolean;
  value: unknown;
  applyOnce: boolean | null;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseExceptions(raw: unknown, field: string, rule: RuleInstance): IWRException[] | null {
  if (raw === undefined) return [];
  if (!Array.isArray(raw)) {
    rule.fail(`${field}: must be an array`);
    return null;
  }
  const out: IWRException[] = [];
  for (const entry of raw) {
    if (typeof entry === "string" && entry) out.push(entry);
    else if (isPlainObject(entry) && isValidPredicate(entry.definition) && typeof entry.label === "string" && entry.label) out.push({ definition: entry.definition, label: entry.label });
    else {
      rule.fail(`${field}: malformed entry`);
      return null;
    }
  }
  return out;
}

/** Parse the shared IWR fields; `kind` says which rule this is. */
export function parseIWR(rule: RuleInstance, kind: IWRKind): IWRRuleData | null {
  const s = rule.source;
  const mode = s.mode === undefined ? "add" : s.mode;
  if (mode !== "add" && mode !== "remove") {
    rule.fail(`mode: "${String(mode)}" is not add or remove`);
    return null;
  }
  const rawTypes = Array.isArray(s.type) ? s.type : [s.type];
  const types = rawTypes.filter((t): t is string => typeof t === "string" && !!t);
  if (types.length === 0 || types.length !== rawTypes.length) {
    rule.fail("type: must have at least 1 type");
    return null;
  }
  if (s.definition !== undefined && !isValidPredicate(s.definition)) {
    rule.fail("definition: must be a predicate");
    return null;
  }
  if (types.includes("custom")) {
    if (types.length > 1) {
      rule.fail('type: "custom" may not be included among other types');
      return null;
    }
    if (!s.definition) {
      rule.fail("definition: must be present if defining a custom type");
      return null;
    }
  } else if (s.definition) {
    rule.fail("definition: may only be present if defining a custom type");
    return null;
  }
  const exceptions = parseExceptions(s.exceptions, "exceptions", rule);
  if (!exceptions) return null;
  if (mode === "remove" && exceptions.length > 0) {
    rule.fail('exceptions: may not be included with a mode of "remove"');
    return null;
  }
  const doubleVs = kind === "resistance" ? parseExceptions(s.doubleVs, "doubleVs", rule) : [];
  if (!doubleVs) return null;
  const value = kind === "immunity" ? null : cleanRuleValue(s.value);
  if (kind !== "immunity" && mode === "add" && value === undefined) {
    rule.fail("value: may not be undefined");
    return null;
  }
  if (kind === "weakness" && typeof s.applyOnce === "boolean" && !types.every((t) => t === "custom")) {
    rule.fail("applyOnce can only be specified for custom weakness types");
    return null;
  }
  return { kind, mode, types, definition: (s.definition as RawPredicate | undefined) ?? null, exceptions, doubleVs, override: s.override === true, value: value ?? null, applyOnce: kind === "weakness" && typeof s.applyOnce === "boolean" ? s.applyOnce : null };
}

function propertyOf(state: EngineState, kind: IWRKind): IWRData[] {
  const attributes = state.actor.system.attributes;
  return kind === "immunity" ? attributes.immunities : kind === "weakness" ? attributes.weaknesses : attributes.resistances;
}

/** Foundry's IWRRuleElement#afterPrepareData: resolve, validate, then add or remove. */
export function applyIWR(rule: RuleInstance, state: EngineState, data: IWRRuleData, options: { override?: boolean; sourceName?: string } = {}): void {
  const types = data.types.map((t) => resolveInjectedProperties(rule, state, t));
  const definition = data.definition ? resolveInjectedProperties(rule, state, structuredClone(data.definition), { warn: false }) : null;
  if (rule.ignored) return;
  const value = data.kind === "immunity" ? null : Math.floor(Number(resolveValue(rule, state, data.value)));
  if (rule.ignored) return;
  const unrecognized = types.filter((t) => !isKnownIWRType(state, data.kind, t));
  if (unrecognized.length > 0) {
    for (const type of unrecognized) rule.fail(`Type "${type}" is unrecognized`);
    return;
  }
  if (data.mode === "add" && data.kind !== "immunity" && (typeof value !== "number" || Number.isNaN(value) || value < 0)) return rule.fail("A `value` must be a positive number");
  const property = propertyOf(state, data.kind);
  const label = state.catalogue?.localize(rule.label) ?? rule.label;
  const sourceName = options.sourceName ?? rule.item.name;
  const override = options.override ?? data.override;
  if (data.mode === "remove") {
    for (const type of types) {
      const index = property.findIndex((iwr) => iwr.type === type);
      if (index !== -1) property.splice(index, 1);
    }
    return;
  }
  if (data.kind !== "immunity" && (value ?? 0) <= 0) return;
  const remaining = [...types];
  for (const type of types) {
    const candidate = createIWR({ kind: data.kind, type, value, definition, exceptions: data.exceptions, doubleVs: data.doubleVs, customLabel: label, source: rule.item.uuid, sourceName, applyOnce: data.applyOnce ?? undefined });
    const current = property.find((iwr) => sameIWR(iwr, candidate, { ignoreExceptions: data.kind === "immunity" && data.exceptions.length === 0 }));
    if (!current) continue;
    if (data.kind === "immunity") {
      remaining.splice(remaining.indexOf(type), 1);
    } else if (override) {
      property.splice(property.indexOf(current), 1);
    } else {
      current.value = Math.max(current.value ?? 0, value ?? 0);
      current.sourceName = label;
      remaining.splice(remaining.indexOf(type), 1);
    }
  }
  for (const type of remaining) {
    property.push(createIWR({ kind: data.kind, type, value, definition, exceptions: data.exceptions, doubleVs: data.doubleVs, customLabel: label, source: rule.item.uuid, sourceName, applyOnce: data.applyOnce ?? undefined }));
  }
}

function handler(kind: IWRKind, key: string): RuleHandler {
  return {
    key,
    parse: (rule) => {
      const data = parseIWR(rule, kind);
      if (data) rule.data = data as unknown as Record<string, unknown>;
    },
    afterPrepareData: (rule, state) => {
      if (!rule.test(state)) return;
      applyIWR(rule, state, rule.data as unknown as IWRRuleData);
    },
  };
}

export const ImmunityHandler = handler("immunity", "Immunity");
export const WeaknessHandler = handler("weakness", "Weakness");
export const ResistanceHandler = handler("resistance", "Resistance");
